// frontend/src/utils/bridgeService.js - Bridge API Service
import config from '../config/bridgeConfig';

/**
 * Bridge Service for interacting with the Relayer API
 * Handles bridge status tracking, user history, and statistics
 */
class BridgeService {
    constructor() {
        // Mainnet-only configuration
        this.baseUrl = process.env.REACT_APP_MAINNET_BRIDGE_API_URL || process.env.REACT_APP_RELAYER_URL || 'http://localhost:3003';
        this.endpoints = config.bridgeApi.endpoints;
        this.pollingIntervals = new Map(); // Track active polling intervals
        
        // Debug: Log the base URL to help identify the issue
        console.log('🔍 BridgeService Debug:', {
            baseUrl: this.baseUrl,
            mainnetUrl: process.env.REACT_APP_MAINNET_BRIDGE_API_URL,
            relayerUrl: process.env.REACT_APP_RELAYER_URL,
            endpoints: this.endpoints
        });
    }

    /**
     * Make API request with error handling
     */
    async makeRequest(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                
                // Try to parse error response as JSON
                let errorData = { message: errorText };
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    // If not JSON, use the text as message
                }
                
                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            // Add more context to the error
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                error.message = `Network error: Unable to connect to ${this.baseUrl}`;
            }
            
            throw error;
        }
    }

    /**
     * Check API health
     */
    async checkHealth() {
        return this.makeRequest(this.endpoints.health);
    }

    /**
     * Get bridge request status by ID
     */
    async getBridgeStatus(requestId) {
        if (!requestId) {
            throw new Error('Request ID is required');
        }
        return this.makeRequest(`${this.endpoints.bridgeStatus}/${requestId}`);
    }

    /**
     * Get user's bridge history
     */
    async getUserBridges(userAddress, options = {}) {
        if (!userAddress) {
            throw new Error('User address is required');
        }

        const { limit = 50, status, network } = options;
        const params = new URLSearchParams();
        
        if (limit) params.append('limit', limit);
        if (status) params.append('status', status);
        if (network) params.append('network', network);

        const queryString = params.toString();
        const endpoint = `${this.endpoints.userBridges}/${userAddress}${queryString ? `?${queryString}` : ''}`;
        
        return this.makeRequest(endpoint);
    }

    /**
     * Get fee statistics from the API
     */
    async getFeeStats() {
        return this.makeRequest(this.endpoints.feeStats);
    }

    /**
     * Get all bridge requests with filtering
     */
    async getBridgeRequests(options = {}) {
        const { status, network, limit = 100, offset = 0 } = options;
        const params = new URLSearchParams();
        
        if (status) params.append('status', status);
        if (network) params.append('network', network);
        if (limit) params.append('limit', limit);
        if (offset) params.append('offset', offset);

        const queryString = params.toString();
        const endpoint = `${this.endpoints.bridgeRequests}${queryString ? `?${queryString}` : ''}`;
        
        return this.makeRequest(endpoint);
    }

    /**
     * Get bridge statistics
     */
    async getBridgeStats() {
        return this.makeRequest(this.endpoints.bridgeStats);
    }

    /**
     * Get network-specific statistics
     */
    async getNetworkStats(network) {
        if (!network) {
            throw new Error('Network parameter is required');
        }
        return this.makeRequest(`${this.endpoints.networkStats}/${network}`);
    }

    /**
     * Start polling bridge status
     */
    startStatusPolling(requestId, callback, interval = config.statusPolling.interval) {
        if (this.pollingIntervals.has(requestId)) {
            return;
        }

        const pollStatus = async () => {
            try {
                const status = await this.pollStatus(requestId, config.statusPolling.maxRetries);
                callback(status);
                
                // Stop polling if completed, failed, or skipped
                if (status.status === 'completed' || status.status === 'failed' || status.status === 'skipped') {
                    this.stopStatusPolling(requestId);
                }
            } catch (error) {
                // Check if it's a timeout or maximum retries exceeded
                if (error.message && error.message.includes('Maximum polling time exceeded')) {
                    console.warn(`⏰ BridgeService: Maximum polling time exceeded for request ${requestId}, stopping polling`);
                    this.stopStatusPolling(requestId);
                    return;
                }
                
                // If request not found (404) after all retries, stop polling
                if (error.status === 404) {
                    console.log(`🛑 BridgeService: Request ${requestId} not found after all retries, stopping polling`);
                    this.stopStatusPolling(requestId);
                    return;
                }
                
                // Continue polling on other errors
                console.error(`⚠️ BridgeService: Error polling status for request ${requestId}:`, error);
            }
        };

        // Initial call with delay
        setTimeout(pollStatus, config.statusPolling.initialDelay);
        
        // Set up interval
        const intervalId = setInterval(pollStatus, interval);
        this.pollingIntervals.set(requestId, intervalId);
    }

    /**
     * Stop polling bridge status
     */
    stopStatusPolling(requestId) {
        const intervalId = this.pollingIntervals.get(requestId);
        if (intervalId) {
            clearInterval(intervalId);
            this.pollingIntervals.delete(requestId);
        }
    }

    /**
     * Stop all active polling
     */
    stopAllPolling() {
        this.pollingIntervals.forEach((intervalId, requestId) => {
            clearInterval(intervalId);
        });
        this.pollingIntervals.clear();
    }

    /**
     * Get active polling requests
     */
    getActivePollingRequests() {
        return Array.from(this.pollingIntervals.keys());
    }

    /**
     * Check if a request is being polled
     */
    isPolling(requestId) {
        return this.pollingIntervals.has(requestId);
    }

    /**
     * Format bridge status for display
     */
    formatBridgeStatus(status) {
        const statusInfo = {
            pending: {
                label: 'Pending',
                color: 'text-blue-600 bg-blue-100',
                icon: '⏳',
                description: 'Waiting for relayer to process'
            },
            processing: {
                label: 'Processing',
                color: 'text-yellow-600 bg-yellow-100',
                icon: '🔄',
                description: 'Relayer is processing with ZK proof'
            },
            completed: {
                label: 'Completed',
                color: 'text-green-600 bg-green-100',
                icon: '✅',
                description: 'Tokens successfully received on destination'
            },
            failed: {
                label: 'Failed',
                color: 'text-red-600 bg-red-100',
                icon: '❌',
                description: 'Processing failed - check error details'
            },
            skipped: {
                label: 'Skipped',
                color: 'text-gray-600 bg-gray-100',
                icon: '⏭️',
                description: 'Request was skipped by relayer'
            }
        };

        return statusInfo[status] || statusInfo.pending;
    }

    /**
     * Format amount with proper decimals
     */
    formatAmount(amount, tokenDecimals = 6) { // tokenDecimals should be provided by caller
        try {
            if (!amount) return '0.000';
            
            // Check if amount is already in human-readable format (has decimal point)
            const amountStr = amount.toString();
            const hasDecimal = amountStr.includes('.');
            
            let numAmount;
            if (hasDecimal) {
                // Amount is already in human-readable format
                numAmount = parseFloat(amount);
            } else {
                // Amount is in wei format, convert to human-readable
                const weiAmount = parseFloat(amount);
                numAmount = weiAmount / Math.pow(10, tokenDecimals);
                
                // If the converted amount is very small (< 0.001) or the original amount is small,
                // it might be that the amount was stored as human-readable but without decimal point
                if ((numAmount < 0.001 && weiAmount > 0) || weiAmount < 100000) {
                    numAmount = weiAmount;
                }
            }
            
            return numAmount.toLocaleString('en-US', {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3
            });
        } catch (error) {
            console.error('Error formatting amount:', error, 'amount:', amount, 'tokenDecimals:', tokenDecimals);
            return '0.000';
        }
    }

    /**
     * Get time since bridge request with proper timezone handling
     */
    getTimeSince(createdAt) {
        if (!createdAt) return 'Unknown';
        
        const created = new Date(createdAt);
        const now = new Date();
        
        // Check if the date is valid
        if (isNaN(created.getTime())) return 'Invalid date';
        
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        let relativeTime;
        if (diffDays > 0) relativeTime = `${diffDays}d ago`;
        else if (diffHours > 0) relativeTime = `${diffHours}h ago`;
        else if (diffMins > 0) relativeTime = `${diffMins}m ago`;
        else relativeTime = 'Just now';
        
        // Format the actual timestamp in user's timezone
        const actualTime = created.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
        
        return `${relativeTime} (${actualTime})`;
    }

    /**
     * Get processing time for completed bridges with timezone-aware formatting
     */
    getProcessingTime(createdAt, processedAt) {
        if (!createdAt || !processedAt) return 'Unknown';
        
        const created = new Date(createdAt);
        const processed = new Date(processedAt);
        
        // Check if dates are valid
        if (isNaN(created.getTime()) || isNaN(processed.getTime())) return 'Invalid date';
        
        const diffMs = processed - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor(diffMs / 1000);
        
        let processingTime;
        if (diffMins > 0) processingTime = `${diffMins}m ${diffSecs % 60}s`;
        else processingTime = `${diffSecs}s`;
        
        // Also show when it was completed in user's timezone
        const completedTime = processed.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        
        return `${processingTime} (completed ${completedTime})`;
    }

    /**
     * Format Ethereum address for display
     */
    formatAddress(address) {
        if (!address) return 'Unknown';
        return address; // Show full address
    }

    async pollStatus(requestId, maxRetries = config.statusPolling.maxRetries) {
        let retries = 0;
        const maxRetriesAllowed = maxRetries;
        const startTime = Date.now();
        const maxTotalTime = config.statusPolling.maxTotalTime || 7200000; // 2 hours default
        
        console.log(`🌐 BridgeService: Starting status polling for request ${requestId} with ${maxRetriesAllowed} max retries (max total time: ${Math.round(maxTotalTime/1000/60)} minutes)`);
        
        while (retries < maxRetriesAllowed) {
            try {
                const currentTime = Date.now();
                const totalTime = currentTime - startTime;
                
                // Check if we've exceeded maximum total time
                if (totalTime > maxTotalTime) {
                    console.warn(`⏰ BridgeService: Maximum polling time exceeded for request ${requestId} (${Math.round(totalTime/1000/60)} minutes), stopping`);
                    throw new Error(`Maximum polling time exceeded: ${Math.round(totalTime/1000/60)} minutes`);
                }
                
                console.log(`🌐 BridgeService: Polling status for request ${requestId} (attempt ${retries + 1}/${maxRetriesAllowed}, total time: ${Math.round(totalTime/1000)}s)`);
                const status = await this.getBridgeStatus(requestId);
                console.log(`✅ BridgeService: Status retrieved successfully for request ${requestId} in ${Math.round(totalTime/1000)}s:`, status);
                return status;
            } catch (error) {
                if (error.status === 404 && retries < maxRetriesAllowed - 1) {
                    // Use configured retry delays or fallback to exponential backoff
                    const delay = config.statusPolling.retryDelays[retries] || Math.pow(2, retries) * 1000;
                    const totalTime = Date.now() - startTime;
                    const totalTimeMinutes = Math.round(totalTime / 1000 / 60);
                    
                    console.log(`⏳ BridgeService: Request ${requestId} not found (404), retrying in ${Math.round(delay/1000)}s (attempt ${retries + 1}/${maxRetriesAllowed}, total time: ${totalTimeMinutes} minutes)`);
                    
                    // Wait before retry
                    await new Promise(resolve => setTimeout(resolve, delay));
                    retries++;
                    continue;
                }
                
                // If it's not a 404 or we've exhausted retries, throw the error
                const totalTime = Date.now() - startTime;
                const totalTimeMinutes = Math.round(totalTime / 1000 / 60);
                console.error(`❌ BridgeService: Failed to get status for request ${requestId} after ${retries + 1} attempts in ${totalTimeMinutes} minutes:`, error);
                throw error;
            }
        }
        
        // This should never be reached due to the throw above, but just in case
        const totalTime = Date.now() - startTime;
        const totalTimeMinutes = Math.round(totalTime / 1000 / 60);
        throw new Error(`Failed to get status for request ${requestId} after ${maxRetriesAllowed} attempts in ${totalTimeMinutes} minutes`);
    }
}

// Create singleton instance
const bridgeService = new BridgeService();

export default bridgeService;
export { BridgeService };
